import React from 'react';
import Chip from '../../shared/Chip';
import ListTable from '../../shared/ListTable';
import { documentNames, documentSummary, plural, scenarioComplete, scenarioUsage } from '../../../../models/LibraryModel';
import ScenarioMenu from './ScenarioMenu';

// A cell is one line (navyDocSummary in the handoff). Several documents read
// as the first plus a count, and the ResUI Tooltip carries every name, since
// the count hides them. One name, cut off, shows in full the same way (the
// table marks `.list-table-cut`). Never a title (ResUI Design Decisions).
function DocumentsCell({ documents, ids }) {
  const names = documentNames(documents, ids);
  if (names.length > 1) return <span className="library-cell" data-tip={names.join(', ')}>{documentSummary(documents, ids)}</span>;
  return <span className="library-cell list-table-cut">{names[0] || '-'}</span>;
}

// An archived scenario keeps its row at full strength; its chip, disabled at
// opacity-60, carries the state, as in the prototype ("The disabled chip
// carries the state instead").
function StatusChip({ scenario }) {
  const ready = scenarioComplete(scenario);
  // The dimming is only visual, so screen readers are told in words.
  return (
    <>
      <Chip
        variant={ready ? 'success' : 'standard'}
        className={scenario.archived ? 'scenario-chip--archived' : undefined}
        aria-disabled={scenario.archived ? 'true' : undefined}
      >
        {ready ? 'Ready' : 'Incomplete'}
      </Chip>
      {scenario.archived && <span className="library-sr-only">, archived</span>}
    </>
  );
}

const usageText = (n) => (n ? `${n} ${plural(n, 'assessment', 'assessments')}` : 'Not in use');

/**
 * The scenarios table (PRD-1701) on the shared List table, as the handoff's
 * "Assessment scenarios": Scenario (pinned), Reference key, Narrative, SOP,
 * Usage, Status and the row menu, sorted from the headers.
 */
function ScenariosTable({ scenarios, documents, sort, onSortChange, resetKey, menuActions }) {
  const names = (ids) => documentNames(documents, ids).join(', ');
  const narrative = (s) => (s.narrativeId ? names([s.narrativeId]) : '');
  const columns = [
    {
      key: 'name',
      title: 'Scenario',
      width: '240px',
      sort: 'text',
      value: (s) => s.name,
      render: (s) => <span className="library-cell library-cell--strong list-table-cut">{s.name}</span>,
    },
    {
      key: 'key',
      title: 'Reference key',
      width: 'minmax(180px, 1fr)',
      sort: 'text',
      value: (s) => names(s.referenceKeyIds),
      render: (s) => <DocumentsCell documents={documents} ids={s.referenceKeyIds} />,
    },
    {
      key: 'narrative',
      title: 'Narrative',
      width: 'minmax(160px, 1fr)',
      sort: 'text',
      value: narrative,
      render: (s) => <span className="library-cell list-table-cut">{narrative(s) || '-'}</span>,
    },
    {
      key: 'sop',
      title: 'SOP',
      width: 'minmax(160px, 1fr)',
      sort: 'text',
      value: (s) => names(s.sopIds),
      render: (s) => <DocumentsCell documents={documents} ids={s.sopIds} />,
    },
    {
      key: 'usage',
      title: 'Usage',
      width: '132px',
      sort: 'number',
      value: scenarioUsage,
      render: (s) => <span className="library-cell">{usageText(scenarioUsage(s))}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      width: '120px',
      sort: 'text',
      value: (s) => (scenarioComplete(s) ? 'Ready' : 'Incomplete'),
      render: (s) => <StatusChip scenario={s} />,
    },
    {
      key: 'actions',
      title: <span className="library-sr-only">Actions</span>,
      width: '48px',
      cellClassName: 'library-cell--menu',
      render: (s) => <ScenarioMenu scenario={s} {...menuActions} />,
    },
  ];
  return (
    <ListTable
      label="Scenarios"
      columns={columns}
      rows={scenarios}
      rowKey={(s) => s.id}
      sort={sort}
      onSortChange={onSortChange}
      resetKey={resetKey}
    />
  );
}

export default ScenariosTable;
